import { Fade, Grid, Typography, useTheme } from '@mui/material';
import React, { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react';
import AnalyticsService from '../../../../shared/services/analytics/analytics.service';
import { COMPARISON_EMISSION_KEY, getComparisonBarSeries, SeriesBar } from '../../../../shared/interfaces/analytics';
import { mergeComparisonByLabel } from '../../../../shared/utils/analytics-utils';
import { getMixedColorPalette } from '../../../../shared/utils/colors-utils'; import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import EmissionService from '../../../../shared/services/emission/emission.service';
import { IEmission } from '../../../../shared/services/emission/emission.model';
import ContactSupplierService from '../../../../shared/services/contact-supplier/contact-supplier.service';
import { IContactSupplierDetails } from '../../../../shared/services/contact-supplier/contact-supplier.model';
import { SupplierPicker } from '../../form-field-components/supplier-picker';
import { GraphDonutChart } from '../graphs/graph-donut-chart';
import { GraphBarChart } from '../graphs/graph-bar-chart';
import { DrillDownDrawer } from '../drill-down-drawer';
import { useDrillDown } from '../use-drill-down';
import { useDrillDimensionLabel } from '../use-drill-dimension-label';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import { useSkeletonDelay } from '../../../../hooks/use-skeleton-delay';
import { AnalyticsBySupplierSkeleton } from './analytics-by-supplier-skeleton';
import { EmissionsTable } from '../../emissons-components/overview/emissions-table';

export const AnalyticsBySupplier: FunctionComponent<{
  reportPeriodId: string;
  emissionType: 'marketBased' | 'locationBased';
  setLoaded?: React.Dispatch<React.SetStateAction<boolean>>;
  /** When set, charts carry the comparison period as an additional series/ring. */
  comparisonPeriod?: { id: string; label: string };
  periodLabel?: string;
}> = ({ reportPeriodId, emissionType, setLoaded, comparisonPeriod, periodLabel }) => {
  // Services
  const analyticsService = new AnalyticsService();
  const emissionService = new EmissionService();
  const contactSupplierService = ContactSupplierService.getInstance();
  // Context
  const reportPeriodContext = useContext(ReportPeriodContext);
  const screenLabelsContext = useContext(ScreenLabelsContext);
  // State
  const [pieSupplierEmissions, setPieSupplierEmissions] = useState<any>();
  const [barCapitalGoodEmissions, setBarCapitalGoodEmissions] = useState<any>();
  const [comparisonPieSupplierEmissions, setComparisonPieSupplierEmissions] = useState<any>();
  const [comparisonBarCapitalGoodEmissions, setComparisonBarCapitalGoodEmissions] = useState<any>();
  const [emissionRows, setEmissionRows] = useState<IEmission[]>([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState<string>('');
  const [allSuppliers, setAllSuppliers] = useState<IContactSupplierDetails[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [inputPage, setInputPage] = useState(0);
  const [perPage, setPerPage] = useState(5);
  const [orderBy, setOrderBy] = useState<string | undefined>();
  const [order, setOrder] = useState<'asc' | 'desc' | undefined>();
  // Colors
  const theme = useTheme();
  const colors = getMixedColorPalette(theme.palette.primary.main);
  // Loading
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const showSkeleton = useSkeletonDelay(isLoading, 250);
  // Variables
  const requiredReportPeriodId = reportPeriodId || reportPeriodContext?.lastSelectedReportId;
  // Click-to-drill: a stale selection clears when the period, basis or comparison changes.
  const scopeTwoMethod = emissionType === 'marketBased' ? 'market' : 'location';
  const drill = useDrillDown([requiredReportPeriodId, emissionType, comparisonPeriod?.id]);
  const dimLabel = useDrillDimensionLabel();

  useEffect(() => {
    if (!requiredReportPeriodId) {
      setIsLoading(false);
      if (setLoaded) setLoaded(true);
      return;
    }

    setIsLoading(true);
    const expectedRequests = comparisonPeriod ? 5 : 3;
    let completedRequests = 0;
    const checkLoadingStatus = () => {
      completedRequests += 1;
      if (completedRequests === expectedRequests) {
        setIsLoading(false);
        if (setLoaded) setLoaded(true);
      }
    };

    analyticsService
      .getPieData(requiredReportPeriodId, `?data=supplier&topN=all`)
      .then((response) => {
        setPieSupplierEmissions(response);
        checkLoadingStatus();
      })
      .catch((err) => {
        console.log(err);
        checkLoadingStatus();
      });

    analyticsService
      .getBarData(requiredReportPeriodId, `?data=good&topN=all`)
      .then((response) => {
        setBarCapitalGoodEmissions(response);
        checkLoadingStatus();
      })
      .catch((err) => {
        console.log(err);
        checkLoadingStatus();
      });

    if (comparisonPeriod) {
      analyticsService
        .getPieData(comparisonPeriod.id, `?data=supplier&topN=all`)
        .then((response) => {
          setComparisonPieSupplierEmissions(response);
          checkLoadingStatus();
        })
        .catch((err) => {
          console.log(err);
          setComparisonPieSupplierEmissions(undefined);
          checkLoadingStatus();
        });

      analyticsService
        .getBarData(comparisonPeriod.id, `?data=good&topN=all`)
        .then((response) => {
          setComparisonBarCapitalGoodEmissions(response);
          checkLoadingStatus();
        })
        .catch((err) => {
          console.log(err);
          setComparisonBarCapitalGoodEmissions(undefined);
          checkLoadingStatus();
        });
    } else {
      setComparisonPieSupplierEmissions(undefined);
      setComparisonBarCapitalGoodEmissions(undefined);
    }

    emissionService
      .getByDomainHighest(
        requiredReportPeriodId,
        'contactSupplier',
        0,
        perPage,
        orderBy,
        order,
      )
      .then((emissionsResponse) => {
        const supplierId = emissionsResponse.rows[0]?.contactSupplierId ?? '';
        setSelectedSupplierId(supplierId);
        setEmissionRows(emissionsResponse.rows);
        setTotalRecords(emissionsResponse.count);
        checkLoadingStatus();
      })
      .catch((err) => {
        console.log(err);
        checkLoadingStatus();
      });
  }, [requiredReportPeriodId, comparisonPeriod?.id]);

  // Bar rows joined on label so both periods render as grouped bars.
  const goodsBarDataset = useMemo(() => {
    const current = barCapitalGoodEmissions?.[emissionType];
    if (!comparisonPeriod || !comparisonBarCapitalGoodEmissions?.[emissionType]) return current;
    return mergeComparisonByLabel(current, comparisonBarCapitalGoodEmissions[emissionType], 'totalEmission', COMPARISON_EMISSION_KEY);
  }, [barCapitalGoodEmissions, comparisonBarCapitalGoodEmissions, comparisonPeriod, emissionType]);

  const goodsBarSeries = comparisonPeriod && comparisonBarCapitalGoodEmissions
    ? getComparisonBarSeries(periodLabel || 'Selected period', comparisonPeriod.label)
    : SeriesBar;

  useEffect(() => {
    contactSupplierService
      .getAllSuppliersLite()
      .then(setAllSuppliers)
      .catch((err) => console.log(err));
  }, []);

  const getEmissionsForSupplier = (
    reportPeriodIdParam: string,
    contactSupplierId: string,
    page: number,
  ) => {
    if (!reportPeriodIdParam || !contactSupplierId) return;
    emissionService
      .getAll(
        reportPeriodIdParam,
        page,
        perPage,
        orderBy,
        order,
        `&contactSupplierId=${contactSupplierId}`,
      )
      .then((response) => {
        setEmissionRows(response.rows);
        setTotalRecords(response.count);
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    if (requiredReportPeriodId) {
      getEmissionsForSupplier(requiredReportPeriodId, selectedSupplierId, inputPage);
    }
  }, [inputPage, perPage, orderBy, order]);

  return (
    <>
      {isLoading && (
        <Fade in={showSkeleton} unmountOnExit>
          <div>
            <AnalyticsBySupplierSkeleton />
          </div>
        </Fade>
      )}
      {!isLoading && (
        <Fade in={!showSkeleton} unmountOnExit>
          <div>
            <Grid container spacing={2}>
              {pieSupplierEmissions && pieSupplierEmissions[emissionType] && (
                <Grid
                  item
                  xl={4}
                  lg={4}
                  md={12}
                  sm={12}
                  xs={12}
                  sx={{ display: 'flex', flexDirection: 'column' }}
                >
                  <GraphDonutChart
                    enableTopN
                    defaultTopN={5}
                    colors={colors}
                    title={`GHG Emission Breakdown by ${screenLabelsContext.getLabel(
                      'supplier',
                      { plural: false, capitalize: 'first' },
                    )}`}
                    tooltip={`The emission breakdown per ${screenLabelsContext.getLabel(
                      'supplier',
                      { plural: false },
                    )} for the selected report period`}
                    dataset={pieSupplierEmissions[emissionType]}
                    periodLabel={periodLabel}
                    comparison={comparisonPeriod && comparisonPieSupplierEmissions?.[emissionType]
                      ? { dataset: comparisonPieSupplierEmissions[emissionType], label: comparisonPeriod.label }
                      : undefined}
                    onSelect={(item) => drill.openDrill({ dimension: 'supplier', id: item.id, label: item.label, value: item.value })}
                  />
                </Grid>
              )}

              {barCapitalGoodEmissions && barCapitalGoodEmissions[emissionType] && (
                <Grid
                  item
                  xl={8}
                  lg={8}
                  md={12}
                  sm={12}
                  xs={12}
                  sx={{ display: 'flex', flexDirection: 'column' }}
                >
                  <GraphBarChart
                    enableTopN
                    defaultTopN={10}
                    title={`GHG Emission Breakdown by ${screenLabelsContext.getLabel(
                      'product',
                      { plural: false, capitalize: 'first' },
                    )}`}
                    tooltip={`The greenhouse gas emission breakdown per ${screenLabelsContext.getLabel(
                      'product',
                      { plural: false },
                    )} for the selected report period`}
                    dataset={goodsBarDataset}
                    series={goodsBarSeries}
                    dataKey="label"
                    layout="horizontal"
                    colors={colors}
                    onSelect={(item) => drill.openDrill({ dimension: 'good', id: item.id, label: item.label, value: item.totalEmission })}
                  />
                </Grid>
              )}

              <Grid
                item
                xl={12}
                lg={12}
                md={12}
                sm={12}
                xs={12}
                sx={{ display: 'flex', flexDirection: 'column' }}
              >
                <Grid container>
                  <Grid item lg={8} sm={12}>
                    <Typography variant="subtitle1" sx={{ flex: 1, mb: 1 }}>
                      <b>
                        Emission Records by{' '}
                        {screenLabelsContext.getLabel('supplier', {
                          plural: false,
                          capitalize: 'first',
                        })}
                      </b>
                    </Typography>
                  </Grid>
                  <Grid item lg={4} sm={12}>
                    {allSuppliers.length > 0 && (
                      <SupplierPicker
                        id="supplier-filter-autocomplete"
                        suppliers={allSuppliers}
                        label="Filter"
                        value={selectedSupplierId || null}
                        onChange={(pickedId) => {
                          const id = pickedId ?? '';
                          setSelectedSupplierId(id);
                          if (id !== selectedSupplierId) setInputPage(0);
                          if (requiredReportPeriodId)
                            getEmissionsForSupplier(requiredReportPeriodId, id, 0);
                        }}
                        size="small"
                        sx={{ width: '100%', mb: 2 }}
                      />
                    )}
                  </Grid>
                </Grid>
                <EmissionsTable
                  emissions={emissionRows}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={totalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  setOrderBy={setOrderBy}
                  setOrder={setOrder}
                  backLink={'/analytics/dashboard?tab=emissions&dim=supplier'}
                  backText="Back to analytics"
                />
              </Grid>
            </Grid>
          </div>
        </Fade>
      )}

      <DrillDownDrawer
        open={drill.open}
        onClose={drill.close}
        selection={drill.selection}
        reportPeriodId={requiredReportPeriodId || ''}
        periodLabel={periodLabel}
        scopeTwoMethod={scopeTwoMethod}
        comparisonReportPeriodId={comparisonPeriod?.id}
        dimensionLabel={dimLabel(drill.selection?.dimension)}
      />
    </>
  );
};
